import type {
  Elemental,
  ElementalFile,
  ElementalType,
  ElementalTypeEnum,
} from '../types/elemental'
import type { PROMPT_TYPES } from '../utils/constants'
import type { APIIntervals, APIOrderings, ItemType } from '../types/api'
import { MOCKED_TYPES } from './@data/elemental-types'
import type { GetUserPromptMode } from './users'
import {
  APIFetch,
  filterResponse,
  filterResponseBoolean,
} from '.'

const IS_API_AVAILABLE = true

// Elemental Types
export const getElementalTypes = async (): Promise<null | ElementalType[]> => {
  const url = '/elemental-types'
  if (!IS_API_AVAILABLE)
    return MOCKED_TYPES

  return filterResponse(
    await APIFetch(url, {
      method: 'GET',
    }),
  )
}

export interface UseElementalData {
  input: string
  styleId?: number
  languageId?: number
  toneId?: number
}

export interface ElementalQuery {
  page?: number
  limit?: number
  topic_id?: number | string
  size?: number
  order_dir?: 'asc' | 'desc'
  order_by?: APIOrderings
  interval?: APIIntervals
  search?: string
  is_featured?: boolean | 'all'
  is_private?: string
  type_ids?: PROMPT_TYPES[]
  enterprise_id?: number
  user_id?: string
  mode?: GetUserPromptMode
  is_premium?: boolean
  companiesSection?: boolean
  item_type?: ItemType
  elemental_type_id?: ElementalTypeEnum
  elemental_type_ids?: ElementalTypeEnum[]
  get_lists?: boolean
}

export interface GetElementalParentsQuery {
  elementIds: (string | number)[]
  limit?: number
}
interface Parent {
  id: number
  title: string
  url: string
}
interface GetElementalParentsResponse {
  id: string
  counts: number
  parents: Parent[]
}
interface CreateElementalResult {
  id: string
  userId: string
  title: string
  template: string
  url: string
  typeId: number
  priceTypeId: number
  isPrivate: true
  isLocked: false
  isPremium: false
  elementalTypeId: ElementalTypeEnum
  companyId: number
}

interface Command {
  elemental_id: string
  id: number
  template: string
  trigger: string
  created_at: string
  updated_at: string
  user_id: string
}

export interface ElemetalInfosData {
  isSaved: boolean
  isVoted: boolean
  counts?: {
    ratings: number
    comments: number
    votes: number
    saves: number
  }
}

export interface ElemetalInfosQuery {
  fields?: 'basic' | 'full'
}

// Elemental Requests
export const getElementals = async (
  query?: ElementalQuery,
  signal?: AbortSignal,

): Promise<null | Elemental[]> => {
  const endpoint = useParseQuery('/elementals', query)
  return filterResponse(
    await APIFetch(endpoint, {
      method: 'GET',
      body: query,
      signal,
    }),
  )
}

export const getElementalParents = async (query: GetElementalParentsQuery) => {
  let url = '/elementals-parents'
  url = useParseQuery(url, query)
  return filterResponse(
    await APIFetch<GetElementalParentsResponse[]>(url, {
      method: 'GET',
    }),
  )
}

export const getElementalsByTrigger = async (
  trigger: string,
): Promise<null | Command> => {
  if (!trigger)
    return null
  return filterResponse(
    await APIFetch(`/commands/${trigger}`, {
      method: 'GET',
    }),
  )
}

export const createElemental = async (
  path: string,
  body: Record<string, any>,
): Promise<null | CreateElementalResult> => {
  return filterResponse(
    await APIFetch(`/${path}`, {
      method: 'POST',
      body,
      timeout: 4000,
    }),
  )
}

export const duplicateElemental = async (
  path: string,
  id: string,
  listId?: string | number,
): Promise<null | Elemental> => {
  return filterResponse<Elemental & { elemental_type_id: string }>(
    await APIFetch(`/${path}/${id}/duplicate`, {
      method: 'POST',
      body: { listId },
    }),
  )
}

export const getElemental = async (
  id: string | number,
  isList?: boolean,
): Promise<null | Elemental> => {
  const path = isList ? 'lists' : 'elementals'
  return filterResponse(await APIFetch(`/${path}/${id}`, {
    method: 'GET',
  }))
}

export const getElementalInfos = async (
  id: string | number,
  query?: ElemetalInfosQuery,
): Promise<null | ElemetalInfosData> => {
  const url = `/elementals/${id}/infos`
  return filterResponse(await APIFetch(useParseQuery(url, query), {
    method: 'GET',
  }))
}

export const updateElemental = async (
  path: string,
  id: string,
  body: Record<string, any>,
): Promise<null | { 'id': string
  'url': string }> => {
  return filterResponse(
    await APIFetch(`/${path}/${id}`, {
      method: 'PATCH',
      body,
      timeout: 4000,
    }),
  )
}

export const deleteElemental = async (
  path: string,
  id: string,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/${path}/${id}`, {
      method: 'DELETE',
    }),
  )
}

export const saveElemental = async (
  path: string,
  id: string,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/${path}/${id}/saves`, {
      method: 'POST',
    }),
  )
}

export const saveElementalInAList = async (
  listId: string | number,
  id: string | number,
  orderIndex?: number,
) => {
  const elemetalRoute = isNaN(Number(id)) ? 'prompts' : 'lists'
  return filterResponse<boolean>(
    await APIFetch(`/lists/${listId}/${elemetalRoute}/${id}`, {
      method: 'POST',
      body: { orderIndex },
    }),
  )
}
export const unsaveElementalInAList = async (
  listId: string | number,
  id: string | number,
  orderIndex?: number,
) => {
  const elemetalRoute = isNaN(Number(id)) ? 'prompts' : 'lists'
  return filterResponse<boolean>(
    await APIFetch(`/lists/${listId}/${elemetalRoute}/${id}`, {
      method: 'DELETE',
      body: { orderIndex },
    }),
  )
}

export const unsaveElemental = async (
  path: string,
  id: string,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/${path}/${id}/saves`, {
      method: 'DELETE',
    }),
  )
}

export const voteElemental = async (
  path: string,
  id: string,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/${path}/${id}/votes`, {
      method: 'POST',
    }),
  )
}

export const removeElementalVote = async (
  path: string,
  id: string,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/${path}/${id}/votes`, {
      method: 'DELETE',
    }),
  )
}

export const updateUseCountElemental = async (
  id: string,
  data: UseElementalData,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(`/prompts/${id}/uses`, {
      method: 'POST',
      body: data,
    }),
  )
}

export const buyElemental = async (
  path: string,
  id: string,
  value?: number,
): Promise<null | string> => {
  return filterResponse(
    await APIFetch<string>(`/${path}/${id}/buy?value=${value}`, {
      method: 'POST',
      baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
    }),
  )
}

export const tipElemental = async (
  type: string,
  id: string,
  value: number,
): Promise<string | null> => {
  // TODO: Improve this type inference
  type = (type !== 'list' && type !== 'lists') ? 'prompt' : 'list'
  let url = `/tips/${id}/${type}`
  url = useParseQuery(url, { value })

  return filterResponse(
    await APIFetch(url, {
      method: 'POST',
      body: { value },
      baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
    }),
  )
}

export const uploadAttachment = async (
  path: string,
  id: string,
  value: File,
  isAdmin?: boolean,
): Promise<string | null> => {
  const url = `${isAdmin ? '/admin' : ''}/${path}/${id}/files`
  const formData = new FormData()
  formData.append('file', value)

  return filterResponse(
    await APIFetch(url, {
      method: 'POST',
      body: formData,
      isFormData: true,
    }),
  )
}

export const getFiles = async (
  path: string,
  productId: string,
): Promise<null | ElementalFile[]> => {
  return filterResponse(
    await APIFetch(`/${path}/${productId}/files`, {
      method: 'GET',
    }),
  )
}

export const getFileLink = async (
  path: string,
  productId: string,
  fileId: number,
): Promise<null | ElementalFile> => {
  return filterResponse(
    await APIFetch(`/${path}/${productId}/files/${fileId}`, {
      method: 'GET',
    }),
  )
}

export const deleteFile = async (
  path: string,
  productId: string,
  fileId: number,
  isAdmin?: boolean,
): Promise<boolean> => {
  return filterResponseBoolean(
    await APIFetch(
      `${isAdmin ? '/admin' : ''}/${path}/${productId}/files/${fileId}`,
      {
        method: 'DELETE',
      },
    ),
  )
}

type UpdateElementalAsAdmin = {
  isHidden?: boolean
  isFeatured?: boolean
  companiesSection?: boolean
} & Record<string, any>
export const updateElementalAsAdmin = async (
  path: string,
  idOrUrl: string,
  body: UpdateElementalAsAdmin,
): Promise<Elemental | null> => {
  return filterResponse(
    await APIFetch(`/admin/${path}/${idOrUrl}`, {
      body,
      method: 'PATCH',
    }),
  )
}
